import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { GraduationCap } from "lucide-react";
import { PageHeader } from "@/components/layout/PageHeader";
import { PageContainer } from "@/components/layout/PageContainer";
import { DataTable, type DataTableColumn } from "@/components/ui/data-table";
import { EmptyState } from "@/components/ui/empty-state";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { binderyApi, type ThesisPrintRequest, type ThesisPrintStatus } from "@/lib/api/bindery";
import { ThesisStatusBadge } from "@/components/bindery/BinderyStatusBadge";
import {
  BINDING_TYPE_LABELS,
  DEGREE_LEVEL_LABELS,
  formatDate,
  formatMoney,
} from "../binderyFormatters";

const STATUS_OPTIONS: ThesisPrintStatus[] = [
  "SUBMITTED",
  "FORMAT_CHECK",
  "APPROVED",
  "PRINTING",
  "BINDING",
  "READY",
  "COLLECTED",
  "REJECTED",
  "CANCELLED",
];

export default function ThesisRequestsPage() {
  const navigate = useNavigate();
  const [data, setData] = useState<ThesisPrintRequest[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await binderyApi.thesis.list({
        search: search || undefined,
        status: statusFilter !== "all" ? (statusFilter as ThesisPrintStatus) : undefined,
        limit: 50,
      });
      setData(res.requests);
      setTotal(res.total);
    } catch (err) {
      console.error("Failed to load thesis requests", err);
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter]);

  useEffect(() => {
    load();
  }, [load]);

  const columns: DataTableColumn<ThesisPrintRequest>[] = [
    {
      id: "number",
      header: "Request",
      cell: (r) => (
        <button
          onClick={() => navigate(`/bindery/thesis/${r.id}`)}
          className="font-mono text-primary hover:underline"
        >
          {r.requestNumber}
        </button>
      ),
    },
    {
      id: "title",
      header: "Title / Patron",
      cell: (r) => (
        <div>
          <p className="line-clamp-1 font-medium text-slate-900">{r.title}</p>
          <p className="text-xs text-slate-500">
            {[r.patron.firstName, r.patron.lastName].filter(Boolean).join(" ") || r.patron.email}
          </p>
        </div>
      ),
    },
    {
      id: "degree",
      header: "Degree / Binding",
      cell: (r) => (
        <div className="text-xs text-slate-600">
          <p>{DEGREE_LEVEL_LABELS[r.degreeLevel]}</p>
          <p className="text-slate-500">{BINDING_TYPE_LABELS[r.bindingType]}</p>
        </div>
      ),
      hideOnMobile: true,
    },
    {
      id: "pages",
      header: "Pages × Copies",
      cell: (r) => (
        <span className="text-sm tabular-nums">
          {r.pageCount} × {r.copies}
        </span>
      ),
      width: "w-28",
      align: "center",
      hideOnMobile: true,
    },
    {
      id: "status",
      header: "Status",
      cell: (r) => <ThesisStatusBadge status={r.status} />,
      width: "w-40",
    },
    {
      id: "submitted",
      header: "Submitted",
      cell: (r) => <span className="text-xs text-slate-500">{formatDate(r.submittedAt)}</span>,
      width: "w-28",
      hideOnMobile: true,
    },
    {
      id: "cost",
      header: "Cost",
      cell: (r) => <span className="text-sm tabular-nums">{formatMoney(r.cost)}</span>,
      width: "w-24",
      align: "right",
      hideOnMobile: true,
    },
  ];

  return (
    <PageContainer width="wide">
      <PageHeader
        title="Thesis Print & Bind"
        description="Review and process student thesis and dissertation print-and-bind requests."
        breadcrumbItems={[{ label: "Dashboard", href: "/dashboard" }, { label: "Thesis requests" }]}
        titleSize="md"
        searchQuery={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search by title, supervisor, number..."
      />

      <div className="flex items-center gap-3">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {STATUS_OPTIONS.map((s) => (
              <SelectItem key={s} value={s}>
                {s.replace(/_/g, " ")}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <span className="text-sm text-slate-500">
          {total} request{total !== 1 ? "s" : ""}
        </span>
      </div>

      <DataTable
        columns={columns}
        data={data}
        rowKey={(r) => r.id}
        loading={loading}
        emptyState={
          <EmptyState
            icon={<GraduationCap className="h-6 w-6 text-slate-400" />}
            title="No thesis requests yet"
            description="Student thesis and dissertation print requests will show up here."
          />
        }
      />
    </PageContainer>
  );
}
